import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { API } from "@/App";
import { 
  GraduationCap, 
  Search, 
  Plus,
  MoreVertical,
  Mail,
  Phone,
  Edit,
  Trash2,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Key,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";

// Available subjects per grade (aligned with NCERT/CBSE curriculum)
const GRADE_SUBJECTS = {
  5: ['Mathematics', 'Science', 'English', 'Hindi'],
  6: ['Mathematics', 'Science', 'English', 'Hindi'],
  7: ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'Hindi'],
  8: ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'Hindi']
};

const GRADES = [5, 6, 7, 8];

export default function SchoolTeachers() {
  const { admin, school } = useOutletContext();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGrades, setExpandedGrades] = useState({});
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [resetPasswordTeacher, setResetPasswordTeacher] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // New teacher form with Grid Assignment structure
  const [newTeacher, setNewTeacher] = useState({
    name: "",
    email: "",
    phone: "",
    designation: "",
    assignments: {} // { "5": ["Mathematics", "Science"], "6": ["Mathematics"] }
  });

  const permissions = admin?.permissions || [];
  const canManage = permissions.includes("manage_teachers");

  // Toggle grade expansion in the grid
  const toggleGradeExpansion = (grade) => {
    setExpandedGrades(prev => ({
      ...prev,
      [grade]: !prev[grade]
    }));
  };

  // Toggle subject for a grade in assignments
  const toggleSubjectForGrade = (grade, subject, isEdit = false) => {
    const setter = isEdit ? setEditingTeacher : setNewTeacher;
    setter(prev => {
      const gradeStr = String(grade);
      const currentAssignments = { ...prev.assignments };
      const currentSubjects = currentAssignments[gradeStr] || [];
      
      if (currentSubjects.includes(subject)) {
        // Remove subject
        const updatedSubjects = currentSubjects.filter(s => s !== subject);
        if (updatedSubjects.length === 0) {
          delete currentAssignments[gradeStr];
        } else {
          currentAssignments[gradeStr] = updatedSubjects;
        }
      } else {
        // Add subject
        currentAssignments[gradeStr] = [...currentSubjects, subject];
      }
      
      return { ...prev, assignments: currentAssignments };
    });
  };

  // Select/Deselect all subjects for a grade
  const toggleAllSubjectsForGrade = (grade, isEdit = false) => {
    const setter = isEdit ? setEditingTeacher : setNewTeacher;
    const current = isEdit ? editingTeacher : newTeacher;
    const gradeStr = String(grade);
    const allSubjects = GRADE_SUBJECTS[grade] || [];
    const currentSubjects = current.assignments[gradeStr] || [];
    const allSelected = currentSubjects.length === allSubjects.length;
    
    setter(prev => {
      const currentAssignments = { ...prev.assignments };
      if (allSelected) {
        // Deselect all
        delete currentAssignments[gradeStr];
      } else {
        // Select all
        currentAssignments[gradeStr] = [...allSubjects];
      }
      return { ...prev, assignments: currentAssignments };
    });
  };

  // Get total assignment count
  const getAssignmentCount = (assignments) => {
    return Object.values(assignments || {}).reduce((sum, subjects) => sum + subjects.length, 0);
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/school/teachers/list`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setTeachers(data.teachers || []);
      } else {
        toast.error("Failed to fetch teachers");
      }
    } catch (error) {
      console.error("Error fetching teachers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();
    
    if (!newTeacher.name || !newTeacher.email) {
      toast.error("Name and Email are required");
      return;
    }

    if (Object.keys(newTeacher.assignments).length === 0) {
      toast.error("Please assign at least one subject to a grade");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/school/teachers/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newTeacher),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Teacher added successfully");
        setShowAddModal(false);
        setNewTeacher({ name: "", email: "", phone: "", designation: "", assignments: {} });
        setExpandedGrades({});
        fetchTeachers();
      } else {
        toast.error(data.error || "Failed to add teacher");
      }
    } catch (error) {
      toast.error("Network error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit teacher
  const handleEditTeacher = async (e) => {
    e.preventDefault();
    
    if (!editingTeacher?.name || !editingTeacher?.email) {
      toast.error("Name and Email are required");
      return;
    }

    if (Object.keys(editingTeacher.assignments || {}).length === 0) {
      toast.error("Please assign at least one subject to a grade");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/school/teachers/${editingTeacher.id}/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: editingTeacher.name,
          email: editingTeacher.email,
          phone: editingTeacher.phone,
          designation: editingTeacher.designation,
          assignments: editingTeacher.assignments
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Teacher updated successfully");
        setShowEditModal(false);
        setEditingTeacher(null);
        setExpandedGrades({});
        fetchTeachers();
      } else {
        toast.error(data.error || "Failed to update teacher");
      }
    } catch (error) {
      toast.error("Network error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete teacher
  const handleDeleteTeacher = async (teacherId) => {
    if (!window.confirm("Are you sure you want to remove this teacher? This action cannot be undone.")) {
      return;
    }

    try {
      const response = await fetch(`${API}/school/teachers/${teacherId}/delete`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Teacher removed successfully");
        fetchTeachers();
      } else {
        toast.error(data.error || "Failed to remove teacher");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  // Handle password reset
  const handleResetPassword = async () => {
    if (!resetPasswordTeacher) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/school/teachers/${resetPasswordTeacher.id}/reset-password`, {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`Password reset email sent to ${resetPasswordTeacher.email}`);
        setShowResetPasswordModal(false);
        setResetPasswordTeacher(null);
      } else {
        toast.error(data.error || "Failed to reset password");
      }
    } catch (error) {
      toast.error("Network error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open edit modal with teacher data
  const openEditModal = (teacher) => {
    setEditingTeacher({
      ...teacher,
      assignments: teacher.assignments || {}
    });
    // Expand grades that have assignments
    const expanded = {};
    Object.keys(teacher.assignments || {}).forEach(grade => {
      expanded[grade] = true;
    });
    setExpandedGrades(expanded);
    setShowEditModal(true);
  };

  const filteredTeachers = teachers.filter(t => 
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Teachers</h1>
          <p className="text-slate-500 text-sm">Manage teaching staff</p>
        </div>
        {canManage && (
          <Button 
            onClick={() => setShowAddModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-teacher-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Teacher
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Teachers Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
        </div>
      ) : filteredTeachers.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-1">No teachers found</h3>
            <p className="text-slate-500 text-sm">
              {searchQuery ? "Try a different search" : "Add your first teacher to get started"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTeachers.map((teacher) => (
            <Card key={teacher.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-lg font-semibold text-purple-600">
                        {teacher.name?.charAt(0) || "T"}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">{teacher.name}</h3>
                      <p className="text-sm text-slate-500">{teacher.designation || "Teacher"}</p>
                    </div>
                  </div>
                  {canManage && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`teacher-menu-${teacher.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditModal(teacher)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          setResetPasswordTeacher(teacher);
                          setShowResetPasswordModal(true);
                        }}>
                          <Key className="w-4 h-4 mr-2" />
                          Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => handleDeleteTeacher(teacher.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{teacher.email}</span>
                  </div>
                  {teacher.phone && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4" />
                      <span>{teacher.phone}</span>
                    </div>
                  )}
                </div>

                {/* Grid Assignments Display */}
                {teacher.assignments && Object.keys(teacher.assignments).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500 font-medium">
                        Assignments ({getAssignmentCount(teacher.assignments)} classes)
                      </span>
                    </div>
                    <div className="space-y-2">
                      {Object.entries(teacher.assignments)
                        .sort(([a], [b]) => Number(a) - Number(b))
                        .map(([grade, subjects]) => (
                          <div key={grade} className="bg-slate-50 rounded-lg p-2">
                            <div className="text-xs font-medium text-slate-700 mb-1">
                              Grade {grade}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {subjects.map(subject => (
                                <Badge 
                                  key={subject} 
                                  variant="secondary" 
                                  className="text-xs bg-white"
                                >
                                  {subject}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Teacher Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => {
        setShowAddModal(open);
        if (!open) {
          setNewTeacher({ name: "", email: "", phone: "", designation: "", assignments: {} });
          setExpandedGrades({});
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Teacher</DialogTitle>
            <DialogDescription>
              Add a teacher and assign subjects by grade
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddTeacher} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input
                  value={newTeacher.name}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Priya Sharma"
                  className="mt-1"
                  data-testid="new-teacher-name"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newTeacher.email}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="teacher@school.edu"
                  className="mt-1"
                  data-testid="new-teacher-email"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label>Phone</Label>
                <Input
                  value={newTeacher.phone}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+91 98765 43210"
                  className="mt-1"
                  data-testid="new-teacher-phone"
                />
              </div>
              <div className="col-span-2">
                <Label>Designation</Label>
                <Input
                  value={newTeacher.designation}
                  onChange={(e) => setNewTeacher(prev => ({ ...prev, designation: e.target.value }))}
                  placeholder="e.g., Senior Mathematics Teacher"
                  className="mt-1"
                  data-testid="new-teacher-designation"
                />
              </div>
            </div>

            {/* Grid Assignment UI */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Subject Assignments by Grade *
              </Label>
              <p className="text-xs text-slate-500">Select the subjects this teacher will teach for each grade</p>
              
              <div className="border rounded-lg divide-y">
                {GRADES.map(grade => {
                  const gradeStr = String(grade);
                  const subjects = GRADE_SUBJECTS[grade];
                  const selectedSubjects = newTeacher.assignments[gradeStr] || [];
                  const isExpanded = expandedGrades[gradeStr];
                  const allSelected = selectedSubjects.length === subjects.length;
                  
                  return (
                    <Collapsible 
                      key={grade} 
                      open={isExpanded}
                      onOpenChange={() => toggleGradeExpansion(gradeStr)}
                    >
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-slate-700">Grade {grade}</span>
                            {selectedSubjects.length > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {selectedSubjects.length} subject{selectedSubjects.length !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-3 pb-3 pt-1 bg-slate-50">
                          <div className="flex items-center justify-between mb-2">
                            <button
                              type="button"
                              onClick={() => toggleAllSubjectsForGrade(grade)}
                              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                              {allSelected ? 'Deselect All' : 'Select All'}
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {subjects.map(subject => (
                              <label 
                                key={subject}
                                className="flex items-center gap-2 p-2 bg-white rounded border cursor-pointer hover:bg-slate-50"
                              >
                                <Checkbox
                                  checked={selectedSubjects.includes(subject)}
                                  onCheckedChange={() => toggleSubjectForGrade(grade, subject)}
                                  data-testid={`grade-${grade}-${subject.toLowerCase().replace(/\s+/g, '-')}`}
                                />
                                <span className="text-sm text-slate-700">{subject}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </div>
              
              {getAssignmentCount(newTeacher.assignments) > 0 && (
                <p className="text-sm text-emerald-600 font-medium">
                  Total: {getAssignmentCount(newTeacher.assignments)} class assignment{getAssignmentCount(newTeacher.assignments) !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={isSubmitting}
                data-testid="submit-add-teacher"
              >
                {isSubmitting ? "Adding..." : "Add Teacher"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Teacher Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => {
        setShowEditModal(open);
        if (!open) {
          setEditingTeacher(null);
          setExpandedGrades({});
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Teacher</DialogTitle>
            <DialogDescription>
              Update teacher details and subject assignments
            </DialogDescription>
          </DialogHeader>
          {editingTeacher && (
            <form onSubmit={handleEditTeacher} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={editingTeacher.name}
                    onChange={(e) => setEditingTeacher(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1"
                    data-testid="edit-teacher-name"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={editingTeacher.email}
                    onChange={(e) => setEditingTeacher(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1"
                    data-testid="edit-teacher-email"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label>Phone</Label>
                  <Input
                    value={editingTeacher.phone || ""}
                    onChange={(e) => setEditingTeacher(prev => ({ ...prev, phone: e.target.value }))}
                    className="mt-1"
                    data-testid="edit-teacher-phone"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Designation</Label>
                  <Input
                    value={editingTeacher.designation || ""}
                    onChange={(e) => setEditingTeacher(prev => ({ ...prev, designation: e.target.value }))}
                    className="mt-1"
                    data-testid="edit-teacher-designation"
                  />
                </div>
              </div>

              {/* Grid Assignment UI for Edit */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Subject Assignments by Grade *
                </Label>
                
                <div className="border rounded-lg divide-y">
                  {GRADES.map(grade => {
                    const gradeStr = String(grade);
                    const subjects = GRADE_SUBJECTS[grade];
                    const selectedSubjects = editingTeacher.assignments?.[gradeStr] || [];
                    const isExpanded = expandedGrades[gradeStr];
                    const allSelected = selectedSubjects.length === subjects.length;
                    
                    return (
                      <Collapsible 
                        key={grade} 
                        open={isExpanded}
                        onOpenChange={() => toggleGradeExpansion(gradeStr)}
                      >
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer">
                            <div className="flex items-center gap-3">
                              <span className="font-medium text-slate-700">Grade {grade}</span>
                              {selectedSubjects.length > 0 && (
                                <Badge variant="secondary" className="text-xs">
                                  {selectedSubjects.length} subject{selectedSubjects.length !== 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="px-3 pb-3 pt-1 bg-slate-50">
                            <div className="flex items-center justify-between mb-2">
                              <button
                                type="button"
                                onClick={() => toggleAllSubjectsForGrade(grade, true)}
                                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                              >
                                {allSelected ? 'Deselect All' : 'Select All'}
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {subjects.map(subject => (
                                <label 
                                  key={subject}
                                  className="flex items-center gap-2 p-2 bg-white rounded border cursor-pointer hover:bg-slate-50"
                                >
                                  <Checkbox
                                    checked={selectedSubjects.includes(subject)}
                                    onCheckedChange={() => toggleSubjectForGrade(grade, subject, true)}
                                  />
                                  <span className="text-sm text-slate-700">{subject}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
                
                {getAssignmentCount(editingTeacher.assignments) > 0 && (
                  <p className="text-sm text-emerald-600 font-medium">
                    Total: {getAssignmentCount(editingTeacher.assignments)} class assignment{getAssignmentCount(editingTeacher.assignments) !== 1 ? 's' : ''}
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={isSubmitting}
                  data-testid="submit-edit-teacher"
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Teacher Password</DialogTitle>
            <DialogDescription>
              A password reset link will be sent to the teacher's email address.
            </DialogDescription>
          </DialogHeader>
          {resetPasswordTeacher && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Teacher:</span> {resetPasswordTeacher.name}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Email:</span> {resetPasswordTeacher.email}
                </p>
              </div>
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                The teacher will receive an email with instructions to set a new password.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowResetPasswordModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleResetPassword}
                  disabled={isSubmitting}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-reset-password"
                >
                  {isSubmitting ? "Sending..." : "Send Reset Link"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
